import React, { useMemo } from "react";
import { Tag, Tooltip, Typography } from "antd";

import { ago } from "@components/Date";
import Identity from "@components/Identity";
import {
  type OrganizationPublicKey,
  type OrganizationPublicKeyAudience,
} from "@api/types";

import { getAudienceColor, getAudienceLabel } from "./audiences";
import TableRowActions from "../TableRowActions";

const LABEL_NAME = "Name";
const LABEL_ID = "Key ID (kid)";
const LABEL_ISSUER = "Issuer (iss)";
const LABEL_AUDIENCE = "API";
const LABEL_STATUS = "Status";
const LABEL_CREATED_BY = "Created By";
const LABEL_LAST_USED_AT = "Last Used";
const LABEL_EMPTY = "-";

const STATUS_ACTIVE = "ACTIVE";

const ID_ELLIPSIS = "...";
const ID_VALUE_LENGTH = 8;
const ID_PREFIX_SEPARATOR = "_";

// NOTE: the ID holds the prefix and a value of 26 characters, and the full
//       value is in the details of the key. The column gives the prefix and
//       the head of the value, therefore it takes less space.
const truncateId = (id: string): string => {
  const separatorIndex = id.indexOf(ID_PREFIX_SEPARATOR);
  const prefixLength = separatorIndex + 1;

  const prefix = id.slice(0, prefixLength);
  const value = id.slice(prefixLength);

  const isValueShort = value.length <= ID_VALUE_LENGTH;

  if (isValueShort) {
    return id;
  }

  const visibleValue = value.slice(0, ID_VALUE_LENGTH);
  const truncatedId = `${prefix}${visibleValue}${ID_ELLIPSIS}`;

  return truncatedId;
};

type Props = {
  onSelected: (selectedPublicKey: OrganizationPublicKey) => void;
  onRevoked: (revokedPublicKey: OrganizationPublicKey) => void;
};

// NOTE: the config keeps its identity while the handlers keep theirs. The
//       table sorts and indexes the items again for each new config, therefore
//       an unstable config makes the table load the items on each render.
const useColumnsConfig = ({ onSelected, onRevoked }: Props) => {
  const columnsConfig = useMemo(() => {
    const nameColumnProps = {
      key: "name",
      title: LABEL_NAME,
      render: (name: string, publicKey: OrganizationPublicKey) => (
        <Typography.Link strong onClick={() => onSelected(publicKey)}>
          {name}
        </Typography.Link>
      ),
      isSearchable: true,
    };

    const idColumnProps = {
      key: "id",
      title: LABEL_ID,
      render: (id: string) => {
        const truncatedId = truncateId(id);

        return (
          <Tooltip title={id}>
            {truncatedId}
          </Tooltip>
        );
      },
    };

    const issuerColumnProps = {
      key: "issuer",
      title: LABEL_ISSUER,
    };

    const audienceColumnProps = {
      key: "audience",
      title: LABEL_AUDIENCE,
      render: (audience: OrganizationPublicKeyAudience) => {
        const label = getAudienceLabel(audience);
        const color = getAudienceColor(audience);

        return <Tag color={color}>{label}</Tag>;
      },
    };

    const createdByColumnProps = {
      key: "createdBy",
      title: LABEL_CREATED_BY,
      render: (userId: string) => <Identity id={userId} />,
    };

    const lastUsedAtColumnProps = {
      key: "lastUsedAt",
      title: LABEL_LAST_USED_AT,
      render: (value?: string) => {
        const hasValue = !!value;

        if (!hasValue) {
          return LABEL_EMPTY;
        }

        return ago(value, true);
      },
    };

    const statusColumnProps = {
      key: "status",
      title: LABEL_STATUS,
      render: (status: string) => {
        const isRevoked = status !== STATUS_ACTIVE;

        if (isRevoked) {
          return <Tag color="red">{status}</Tag>;
        }

        return <Tag color="green">{status}</Tag>;
      },
    };

    const actionsColumnProps = {
      key: "actions",
      title: false,
      width: 48,
      render: (_: unknown, publicKey: OrganizationPublicKey) => (
        <TableRowActions
          publicKey={publicKey}
          onRevoked={onRevoked}
        />
      ),
    };

    return [
      { ...nameColumnProps },
      { ...audienceColumnProps },
      { ...idColumnProps },
      { ...issuerColumnProps },
      { ...lastUsedAtColumnProps },
      { ...statusColumnProps },
      { ...createdByColumnProps },
      { ...actionsColumnProps },
    ];
  }, [
    onRevoked,
    onSelected,
  ]);

  return columnsConfig;
};

export default useColumnsConfig;
